import { useCallback, useRef } from 'react'
import type React from 'react'

/**
 * 손을 떼는 순간 처리하는 탭. 누르는 자리마다 쓸 수 있게 뽑아 둔 것이다.
 *
 * **왜 `onClick` 으로는 안 되는가**
 *
 * 아이폰 웹뷰는 손이 떨어진 뒤에도 '탭인가 스크롤인가'를, 그리고 '두 번
 * 쳐서 확대하려나'를 한 번 더 가늠하고서야 `click` 을 보낸다. 그래서
 *   - 반응이 눈에 띄게 늦고,
 *   - 빠르게 연달아 누르면 두 번째 탭이 제스처 후보로 묶여 **아예 삼켜진다.**
 *
 * `touch-action: manipulation` 으로 막으려 해 봤지만 아이폰은 그 지시를
 * 지키지 않았다(사진 고르는 칸이 그래서 안 됐다). 확실한 방법은 포인터를
 * 직접 따라가 **손 떼는 순간 끝내고, 뒤따라올 `click` 은 버리는 것**이다.
 *
 * **쓰는 법** — 반환값을 버튼에 그대로 펼친다.
 *
 * ```tsx
 * const tap = useTap(() => onChange(!checked))
 * return <button type="button" {...tap}>…</button>
 * ```
 *
 * 스크롤·쓸어넘김과 섞여도 안전하다. 손가락이 `SLOP_PX` 보다 많이 밀리면
 * 탭으로 치지 않으므로, 목록 위에서 쓸어넘기다가 눌리는 일이 없다.
 * 키보드(Enter·Space)로 온 활성화는 `click` 으로만 오므로 그쪽도 받는다.
 */

/** 이만큼 밀리면 탭이 아니라 쓸어넘김으로 본다. 사진 칸(Collect)과 같은 값. */
const SLOP_PX = 12

export interface TapHandlers {
  onPointerDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: () => void
  onPointerCancel: () => void
  onTouchEnd: (e: React.TouchEvent) => void
  onClick: (e: React.MouseEvent) => void
  style: React.CSSProperties
}

export function useTap(onTap: () => void): TapHandlers {
  /** 누르기 시작한 자리. 비어 있으면 '이미 탭이 아닌 것으로 판정됐다'는 뜻. */
  const fromRef = useRef<{ x: number; y: number } | null>(null)

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    fromRef.current = { x: e.clientX, y: e.clientY }
    try {
      // 손가락이 칸 밖으로 밀려도 move·up 이 계속 이 칸으로 온다
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* 못 붙잡아도 탭 자체는 돈다 */
    }
  }, [])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const from = fromRef.current
    if (!from) return
    const dx = e.clientX - from.x
    const dy = e.clientY - from.y
    if (dx * dx + dy * dy > SLOP_PX * SLOP_PX) fromRef.current = null
  }, [])

  const onPointerUp = useCallback(() => {
    const wasTap = fromRef.current !== null
    fromRef.current = null
    if (wasTap) onTap()
  }, [onTap])

  const onPointerCancel = useCallback(() => {
    fromRef.current = null
  }, [])

  /*
    아이폰이 연속 탭을 '두 번 쳐서 확대'로 묶는 것을 막는다. 기본 동작을
    막으면 그 묶음이 아예 생기지 않는다. 뒤따라올 click 도 같이 사라지는데,
    처리는 이미 pointerup 에서 끝냈으니 잃는 것이 없다.
  */
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
  }, [])

  /**
   * 키보드로 온 활성화만 여기서 처리한다.
   *
   * 손가락·마우스가 만든 click 은 버린다. "포인터에서 이미 처리했다"는
   * 깃발을 세워 두고 삼키는 방법은 **빠른 연속 탭에서 무너진다** — 늦게
   * 도착한 click 이 다음 탭의 시작에 깃발이 지워진 뒤 들어와 직전 선택을
   * 되돌린다. 그래서 깃발을 쓰지 않고 **그 click 하나만 보고** 가른다.
   *   - 키보드: `detail === 0`, `pointerType` 이 비어 있다
   *   - 손가락·마우스: `detail >= 1`, `pointerType` 은 'touch'·'mouse'
   */
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      const native = e.nativeEvent as PointerEvent
      const fromPointer =
        e.detail > 0 || ('pointerType' in native && !!native.pointerType)
      if (fromPointer) return
      onTap()
    },
    [onTap],
  )

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onTouchEnd,
    onClick,
    // 누르는 중에도 세로 스크롤은 살아 있어야 한다
    style: { touchAction: 'pan-y' },
  }
}
